<template>
  <el-drawer
    :visible="visible"
    :show-close="true"
    :wrapper-closable="true"
    :before-close="onBeforeClose"
    size="520px"
    append-to-body
    custom-class="complaint-drawer live-case-drawer"
    @close="$emit('close')"
  >
    <template #title>
      <div class="complaint-drawer__title">
        <span class="complaint-drawer__title-text">{{ headerTitle }}</span>
        <p v-if="row" class="complaint-drawer__subtitle">{{ drawerSubtitle }}</p>
        <div v-if="row" class="complaint-drawer__title-tags">
          <el-tag v-for="tag in statusTags" :key="tag.label" size="mini" :type="tag.type">{{ tag.label }}</el-tag>
        </div>
      </div>
    </template>

    <div v-if="row" class="live-drawer__shell">
      <div class="live-drawer__body">
        <section class="live-drawer__section">
          <header class="live-drawer__section-head">
            <h3 class="live-drawer__section-title">处理信息</h3>
          </header>
          <el-form
            label-position="left"
            :label-width="liveDrawerFormLabelWidth"
            :style="liveDrawerFormStyle"
            size="small"
            class="live-drawer__form"
          >
            <div class="live-drawer__row">
              <span class="live-drawer__label">投诉日期</span>
              <div class="live-drawer__control">
                <el-date-picker
                  v-model="draft.date"
                  type="date"
                  value-format="yyyy-MM-dd"
                  class="live-drawer__date"
                  :append-to-body="true"
                  placeholder="选择日期"
                  :disabled="readonly || fieldsFrozen"
                  @change="onComplaintDateChange"
                />
              </div>
              <span class="live-drawer__label">完成计划日</span>
              <div class="live-drawer__control">
                <el-date-picker
                  v-model="draft.planDate"
                  type="date"
                  value-format="yyyy-MM-dd"
                  class="live-drawer__date"
                  :append-to-body="true"
                  placeholder="选择日期"
                  :disabled="readonly || planFrozen"
                />
                <ConflictPick
                  v-if="conflict('planDate')"
                  :conflict="conflict('planDate')"
                  @pick="onPick('planDate', $event)"
                />
              </div>
            </div>
            <div class="live-drawer__row">
              <el-form-item label="异常投诉">
                <el-input v-model="draft.yc" type="textarea" :rows="2" :disabled="readonly" />
                <ConflictPick v-if="conflict('yc')" :conflict="conflict('yc')" @pick="onPick('yc', $event)" />
              </el-form-item>
            </div>
            <div class="live-drawer__row">
              <span class="live-drawer__label">生产部门</span>
              <div class="live-drawer__control">
                <el-select
                  v-model="draft.deptP"
                  class="live-drawer__full"
                  filterable
                  clearable
                  :popper-append-to-body="true"
                  placeholder="选择生产部门"
                  :disabled="readonly"
                >
                  <el-option v-for="name in deptOptions" :key="name" :label="name" :value="name" />
                </el-select>
                <ConflictPick v-if="conflict('deptP')" :conflict="conflict('deptP')" @pick="onPick('deptP', $event)" />
              </div>
              <span class="live-drawer__label">负责人</span>
              <div class="live-drawer__control">
                <el-select
                  v-model="fzrList"
                  class="live-drawer__full live-drawer__fzr-select"
                  multiple
                  collapse-tags
                  filterable
                  clearable
                  :popper-append-to-body="true"
                  placeholder="选择负责人"
                  :disabled="readonly"
                >
                  <el-option v-for="name in fzrOptions" :key="name" :label="name" :value="name" />
                </el-select>
                <ConflictPick v-if="conflict('fzr')" :conflict="conflict('fzr')" @pick="onPick('fzr', $event)" />
              </div>
            </div>
            <div class="live-drawer__row">
              <el-form-item label="原因分析">
                <el-input v-model="draft.cause" type="textarea" :rows="2" :disabled="readonly" />
                <ConflictPick v-if="conflict('cause')" :conflict="conflict('cause')" @pick="onPick('cause', $event)" />
              </el-form-item>
            </div>
            <div class="live-drawer__row">
              <el-form-item label="临时对策">
                <el-input v-model="draft.temp" type="textarea" :rows="2" :disabled="readonly" />
                <ConflictPick v-if="conflict('temp')" :conflict="conflict('temp')" @pick="onPick('temp', $event)" />
              </el-form-item>
            </div>
            <div class="live-drawer__row">
              <el-form-item label="永久对策">
                <el-input v-model="draft.permanent" type="textarea" :rows="2" :disabled="readonly" />
                <ConflictPick
                  v-if="conflict('permanent')"
                  :conflict="conflict('permanent')"
                  @pick="onPick('permanent', $event)"
                />
              </el-form-item>
            </div>
          </el-form>
        </section>

        <section class="live-drawer__section live-drawer__section--muted">
          <header class="live-drawer__section-head">
            <h3 class="live-drawer__section-title">进度节点</h3>
            <p class="live-drawer__section-desc">计划在上、工位在下；未填点击记今天，已填点击打开详情</p>
          </header>
          <div class="live-drawer__op-stages">
            <LiveEditorOpStageCell
              :plan-text="replyPlanText"
              label="回复"
              :value="row.replyActual"
              :done="Boolean(row.replyActual)"
              :interactive="opCellInteractive"
              @click="onOpCellClick('reply')"
            />
            <LiveEditorOpStageCell
              :plan-text="replyPlanText"
              label="完成"
              :value="row.defectActual"
              :done="Boolean(row.defectActual)"
              :loading="switchBusy === 'defect'"
              :interactive="opCellInteractive && Boolean(row.id)"
              @click="onOpCellClick('defect')"
            />
            <LiveEditorOpStageCell
              :plan-text="closePlanText"
              label="结案"
              :value="row.closeActual"
              :done="Boolean(row.closeActual)"
              :loading="switchBusy === 'close'"
              :interactive="opCellInteractive"
              @click="onOpCellClick('close')"
            />
          </div>
        </section>
      </div>

      <footer v-if="!readonly" class="live-drawer__footer">
        <div class="live-drawer__footer-side live-drawer__footer-side--end">
          <el-button size="small" @click="requestClose">关闭</el-button>
          <el-button size="small" type="primary" :loading="saving" @click="onSave">保存</el-button>
        </div>
      </footer>
    </div>

    <LiveEditorOpStageDialog
      :visible.sync="opDialog.visible"
      :mode="opDialog.mode"
      :plan-default="opDialog.planDefault"
      :meta="opDialog.meta"
      :readonly="opDialog.readonly"
      :plan-readonly="opDialog.planReadonly"
      :confirming="opDialog.confirming"
      :show-close-case="opDialog.showCloseCase"
      :initial="opDialog.initial"
      @confirm="onOpDialogConfirm"
      @close-case="onCloseCaseFromDialog"
    />
  </el-drawer>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';

import {
  closeLiveCase,
  fetchLiveCases,
  markLiveDefect,
  patchLiveCase,
  resolveLiveConflict,
  undoLiveDefect,
  type LiveCase,
} from '@/api/live-case';
import LiveEditorOpStageCell from '@/components/live-editor/op-stage-cell.vue';
import LiveEditorOpStageDialog, {
  type LiveEditorOpDialogForm,
  type LiveEditorOpDialogMeta,
  type LiveEditorOpDialogMode,
} from '@/components/live-editor/op-stage-dialog.vue';
import { complaintDrawerSubtitle, complaintPartTitle } from '@/domain/complaint-present';
import {
  addCalendarDays,
  hasConflicts,
  liveClosePlanDateForRow,
  liveLanes,
  liveReplyPlanDateForRow,
  LIVE_CLOSE_PLAN_DAYS,
  LIVE_REPLY_PLAN_DAYS,
  type LiveCasePatch,
  type LiveConflictField,
} from '@/domain/live-case';
import { SIM_TODAY } from '@/utils/time';
import { joinFzrList, parseFzrList, uniqueFzrOptions } from '@/views/live-editor/fzr';
import {
  closePlanDateRaw,
  stagePlanDateForOp,
  stagePlanDefault,
  type StagePlanRow,
} from '@/views/live-editor/plan-defaults';

/** 抽屉 label 列宽：同时作用于 el-form 与自定义网格，只改这一处 */
const LIVE_DRAWER_FORM_LABEL_WIDTH = '70px';

const ConflictPick = Vue.extend({
  name: 'ConflictPick',
  props: {
    conflict: { type: Object as PropType<{ system: string; excel: string }>, required: true },
  },
  template: `
    <div class="conflict-pick">
      <span>系统：{{ conflict.system }}</span>
      <span>Excel：{{ conflict.excel }}</span>
      <el-button size="mini" @click="$emit('pick', 'system')">留系统</el-button>
      <el-button size="mini" @click="$emit('pick', 'excel')">留 Excel</el-button>
    </div>
  `,
});

type LiveDrawerDraft = LiveCasePatch & {
  date: string;
  planDate: string;
  replyPlanDate: string | null;
  yc: string;
  deptP: string;
  fzr: string;
};

function stagePlanRowFromCase(row: LiveCase, draft?: LiveDrawerDraft): StagePlanRow {
  return {
    date: draft?.date || row.date,
    replyPlanDate: draft?.replyPlanDate ?? row.replyPlanDate,
    planDate: draft?.planDate ?? row.planDate,
  };
}

function toDraft(row: LiveCase): LiveDrawerDraft {
  return {
    date: row.date,
    yc: row.yc,
    deptP: row.deptP,
    fzr: row.fzr,
    planDate: row.planDate,
    replyPlanDate: row.replyPlanDate,
    deptC: row.deptC,
    jz: row.jz,
    pm: row.pm,
    lh: row.lh,
    cause: row.cause,
    temp: row.temp,
    permanent: row.permanent,
  };
}

function draftEquals(row: LiveCase | null, draft: LiveDrawerDraft): boolean {
  if (!row) return true;
  return JSON.stringify(toDraft(row)) === JSON.stringify(draft);
}

const emptyOpForm: LiveEditorOpDialogForm = {
  stagePlanDate: null,
  replyActual: null,
  cause: '',
  temp: '',
  defectActual: null,
  closeActual: null,
};

export default Vue.extend({
  name: 'LiveCaseDrawer',
  components: { ConflictPick, LiveEditorOpStageCell, LiveEditorOpStageDialog },
  props: {
    visible: { type: Boolean, default: false },
    row: { type: Object as PropType<LiveCase | null>, default: null },
    /** 父页已加载的 live_cases，用于生产部门/负责人下拉；不传则抽屉内自行请求 */
    optionCases: { type: Array as PropType<LiveCase[]>, default: () => [] },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      draft: {} as LiveDrawerDraft,
      saving: false,
      optionRows: [] as LiveCase[],
      switchBusy: null as 'defect' | 'close' | null,
      opDialog: {
        visible: false,
        mode: 'reply' as LiveEditorOpDialogMode,
        planDefault: '',
        meta: {} as LiveEditorOpDialogMeta,
        readonly: false,
        planReadonly: false,
        confirming: false,
        showCloseCase: false,
        initial: { ...emptyOpForm },
      },
    };
  },
  computed: {
    liveDrawerFormLabelWidth(): string {
      return LIVE_DRAWER_FORM_LABEL_WIDTH;
    },
    liveDrawerFormStyle(): Record<string, string> {
      return { '--live-drawer-label-width': LIVE_DRAWER_FORM_LABEL_WIDTH };
    },
    selectOptionRows(): LiveCase[] {
      return this.optionCases.length ? this.optionCases : this.optionRows;
    },
    deptOptions(): string[] {
      const set = new Set<string>();
      this.selectOptionRows.forEach((row) => {
        const text = String(row.deptP || '').trim();
        if (text) set.add(text);
      });
      const current = String(this.draft.deptP || '').trim();
      if (current) set.add(current);
      return Array.from(set).sort((a, b) => a.localeCompare(b, 'zh-CN'));
    },
    fzrOptions(): string[] {
      const values = this.selectOptionRows.map((row) => row.fzr);
      values.push(this.draft.fzr);
      return uniqueFzrOptions(values);
    },
    fzrList: {
      get(): string[] {
        return parseFzrList(this.draft.fzr);
      },
      set(values: string[]) {
        this.draft.fzr = joinFzrList(values);
      },
    },
    headerTitle(): string {
      if (!this.row) return '投诉处理';
      return complaintPartTitle(this.row);
    },
    drawerSubtitle(): string {
      if (!this.row) return '';
      return complaintDrawerSubtitle(this.row);
    },
    statusTags(): Array<{ label: string; type: 'success' | 'warning' | 'danger' | 'info' | 'primary' }> {
      if (!this.row) return [];
      const tags: Array<{ label: string; type: 'success' | 'warning' | 'danger' | 'info' | 'primary' }> = [];
      if (hasConflicts(this.row)) tags.push({ label: '待确认', type: 'warning' });
      if (this.row.closeActual) {
        tags.push({ label: '已结案', type: 'success' });
        return tags;
      }
      const lanes = liveLanes(this.row, SIM_TODAY);
      if (lanes.unreplied) tags.push({ label: lanes.replyOverdue ? '超期未回复' : '未回复', type: 'danger' });
      if (lanes.handling) tags.push({ label: lanes.closeOverdue ? '超期未结案' : '处理中', type: 'primary' });
      if (lanes.defectOpen) tags.push({ label: '不良品未处理', type: 'warning' });
      return tags;
    },
    closed(): boolean {
      return Boolean(this.row?.closeActual);
    },
    planFrozen(): boolean {
      return this.closed;
    },
    fieldsFrozen(): boolean {
      return this.closed;
    },
    opCellInteractive(): boolean {
      return !this.readonly;
    },
    replyPlanText(): string {
      if (!this.row) return '—';
      const planRow = stagePlanRowFromCase(this.row, this.draft);
      return liveReplyPlanDateForRow(planRow) || '—';
    },
    closePlanText(): string {
      if (!this.row) return '—';
      const planRow = stagePlanRowFromCase(this.row, this.draft);
      return liveClosePlanDateForRow({ date: planRow.date, planDate: closePlanDateRaw(planRow) }) || '—';
    },
    isDirty(): boolean {
      if (this.readonly || !this.row) return false;
      return !draftEquals(this.row, this.draft);
    },
  },
  watch: {
    visible(open: boolean) {
      if (open) this.loadSelectOptions();
    },
    row: {
      handler(value: LiveCase | null) {
        if (value) this.draft = toDraft(value);
      },
      immediate: true,
    },
  },
  methods: {
    loadSelectOptions() {
      if (this.optionCases.length) return;
      fetchLiveCases()
        .then((rows) => {
          this.optionRows = rows;
        })
        .catch((err: Error) => {
          this.$message.error(err.message || '加载部门/负责人选项失败');
        });
    },
    conflict(field: LiveConflictField) {
      return this.row?.conflicts?.[field] ?? null;
    },
    onPick(field: LiveConflictField, side: 'system' | 'excel') {
      if (!this.row) return;
      resolveLiveConflict(this.row.id, field, side).then((saved) => {
        this.$emit('saved', saved);
      });
    },
    onSave() {
      if (!this.row) return;
      this.saving = true;
      patchLiveCase(this.row.id, this.draft)
        .then((saved) => {
          this.$message.success('已保存');
          this.draft = toDraft(saved);
          this.$emit('saved', saved);
        })
        .catch((err: Error) => this.$message.error(err.message))
        .then(() => {
          this.saving = false;
        });
    },
    confirmDiscardIfDirty(): Promise<boolean> {
      if (!this.isDirty) return Promise.resolve(true);
      return this.$confirm('有未保存的修改，确定要关闭吗？', '提示', {
        confirmButtonText: '放弃修改',
        cancelButtonText: '继续编辑',
        type: 'warning',
        closeOnClickModal: false,
      })
        .then(() => {
          if (this.row) this.draft = toDraft(this.row);
          return true;
        })
        .catch(() => false);
    },
    onBeforeClose(done: () => void) {
      this.confirmDiscardIfDirty().then((ok) => {
        if (ok) done();
      });
    },
    requestClose() {
      this.confirmDiscardIfDirty().then((ok) => {
        if (ok) this.$emit('close');
      });
    },
    onComplaintDateChange(value: string) {
      if (!value || this.closed) return;
      this.draft.replyPlanDate = addCalendarDays(value, LIVE_REPLY_PLAN_DAYS);
      if (!this.planFrozen) {
        this.draft.planDate = addCalendarDays(value, LIVE_CLOSE_PLAN_DAYS);
      }
    },
    applySaved(saved: LiveCase) {
      this.draft = toDraft(saved);
      this.$emit('saved', saved);
    },
    replyPlanDatePatch(value: string | null | undefined): string | null {
      const planRow = this.row ? stagePlanRowFromCase(this.row, this.draft) : { date: '' };
      const text = String(value || '').trim();
      const fallback = stagePlanDefault('reply', planRow);
      return !text || text === fallback ? null : text;
    },
    onOpCellClick(mode: LiveEditorOpDialogMode) {
      if (!this.row || this.readonly) return;
      const row = this.row;
      if (mode === 'reply') {
        if (!row.closeActual && !row.replyActual) {
          this.quickReplyToday();
          return;
        }
        this.openOpDialog(mode);
        return;
      }
      if (mode === 'defect') {
        if (!row.closeActual && !row.defectActual && row.id) {
          this.onDefectSwitch(true);
          return;
        }
        this.openOpDialog(mode);
        return;
      }
      if (!row.closeActual && this.canToggleClose()) {
        this.onCloseSwitch(true);
        return;
      }
      this.openOpDialog(mode);
    },
    openOpDialog(mode: LiveEditorOpDialogMode) {
      if (!this.row) return;
      const row = this.row;
      const planRow = stagePlanRowFromCase(row, this.draft);
      const readonly = Boolean(row.closeActual) && mode !== 'close';
      const planReadonly = Boolean(row.closeActual);
      const planDefault = stagePlanDefault(mode, planRow);
      const stagePlanDate = stagePlanDateForOp(mode, planRow);
      this.opDialog = {
        ...this.opDialog,
        visible: true,
        mode,
        planDefault,
        meta: {
          date: this.draft.date || row.date,
          yc: this.draft.yc || row.yc,
          deptP: this.draft.deptP || row.deptP,
          fzr: this.draft.fzr || row.fzr,
          pm: row.pm,
          lh: row.lh,
        },
        readonly,
        planReadonly,
        confirming: false,
        showCloseCase: mode === 'close' && !row.closeActual && this.canToggleClose(),
        initial: {
          stagePlanDate: stagePlanDate || null,
          replyActual: row.replyActual || null,
          cause: this.draft.cause || row.cause || '',
          temp: this.draft.temp || row.temp || '',
          defectActual: row.defectActual || null,
          closeActual: row.closeActual || null,
        },
      };
    },
    onOpDialogConfirm(form: LiveEditorOpDialogForm) {
      if (!this.row) {
        this.opDialog.visible = false;
        return;
      }
      const mode = this.opDialog.mode;
      if (mode === 'reply') {
        if (this.opDialog.readonly) {
          this.opDialog.visible = false;
          return;
        }
        this.applyReplyDialog(form);
        return;
      }
      if (mode === 'close') {
        if (this.opDialog.planReadonly) {
          this.opDialog.visible = false;
          return;
        }
        this.applyCloseDialog(form);
        return;
      }
      if (!this.opDialog.readonly) {
        this.applyDefectDialog(form);
        return;
      }
      this.opDialog.confirming = false;
      this.opDialog.visible = false;
    },
    quickReplyToday() {
      if (!this.row) return;
      patchLiveCase(this.row.id, { replyActual: SIM_TODAY })
        .then((saved) => this.applySaved(saved))
        .catch((err: Error) => this.$message.error(err.message));
    },
    applyReplyDialog(form: LiveEditorOpDialogForm) {
      if (!this.row) return;
      this.opDialog.confirming = true;
      const patch: LiveCasePatch = {
        replyActual: form.replyActual ? form.replyActual : null,
        cause: form.cause || '',
        temp: form.temp || '',
        replyPlanDate: this.replyPlanDatePatch(form.stagePlanDate),
      };
      patchLiveCase(this.row.id, patch)
        .then((saved) => {
          this.applySaved(saved);
          this.opDialog.visible = false;
        })
        .catch((err: Error) => this.$message.error(err.message))
        .then(() => {
          this.opDialog.confirming = false;
        });
    },
    applyCloseDialog(form: LiveEditorOpDialogForm) {
      if (!this.row) return;
      const planRow = stagePlanRowFromCase(this.row, this.draft);
      const plan =
        String(form.stagePlanDate || '').trim() || stagePlanDefault('close', planRow);
      if (!plan) {
        this.opDialog.visible = false;
        return;
      }
      this.opDialog.confirming = true;
      patchLiveCase(this.row.id, { planDate: plan })
        .then((saved) => {
          this.applySaved(saved);
          this.opDialog.visible = false;
        })
        .catch((err: Error) => this.$message.error(err.message))
        .then(() => {
          this.opDialog.confirming = false;
        });
    },
    applyDefectDialog(form: LiveEditorOpDialogForm) {
      if (!this.row) return;
      const row = this.row;
      const planRow = stagePlanRowFromCase(row, this.draft);
      const prevPlan = liveReplyPlanDateForRow(planRow);
      const nextReplyPlan = this.replyPlanDatePatch(form.stagePlanDate);
      const planChanged =
        prevPlan !==
        liveReplyPlanDateForRow({
          date: planRow.date,
          replyPlanDate: nextReplyPlan,
        });
      const nextDate = form.defectActual ? form.defectActual : null;
      const prev = row.defectActual || null;
      const defectChanged = nextDate !== prev;

      if (!defectChanged && !planChanged) {
        this.opDialog.visible = false;
        return;
      }

      this.opDialog.confirming = true;
      const patch: LiveCasePatch = {};
      if (defectChanged) patch.defectActual = nextDate;
      if (planChanged) patch.replyPlanDate = nextReplyPlan;
      patchLiveCase(row.id, patch)
        .then((saved) => {
          this.applySaved(saved);
          this.opDialog.visible = false;
        })
        .catch((err: Error) => this.$message.error(err.message))
        .then(() => {
          this.opDialog.confirming = false;
        });
    },
    canToggleClose(): boolean {
      return Boolean(this.row?.id) && !this.row?.closeActual && !this.switchBusy;
    },
    onCloseCaseFromDialog() {
      if (!this.row || this.opDialog.confirming) return;
      this.opDialog.confirming = true;
      this.onCloseSwitch(true, { fromOpDialog: true });
    },
    onDefectSwitch(on: boolean) {
      if (!this.row?.id || this.closed) return;
      const wasOn = Boolean(this.row.defectActual);
      if (on === wasOn) return;
      this.switchBusy = 'defect';
      const action = on ? markLiveDefect : undoLiveDefect;
      action(this.row.id)
        .then((saved) => this.applySaved(saved))
        .catch((err: Error) => this.$message.error(err.message))
        .then(() => {
          this.switchBusy = null;
        });
    },
    onCloseSwitch(on: boolean, options?: { fromOpDialog?: boolean }) {
      if (!this.row?.id || !on || this.closed) {
        if (options?.fromOpDialog) this.opDialog.confirming = false;
        return;
      }
      this.$confirm('结案后完成计划日与实际日不可再改，确定结案？', '结案确认', {
        type: 'warning',
        confirmButtonText: '结案',
        cancelButtonText: '取消',
      })
        .then(() => {
          this.switchBusy = 'close';
          return closeLiveCase(this.row!.id);
        })
        .then((saved) => {
          this.$message.success('已结案');
          this.applySaved(saved);
          if (options?.fromOpDialog) this.opDialog.visible = false;
        })
        .catch((err: unknown) => {
          if (err === 'cancel' || err === 'close') return;
          if (err instanceof Error) this.$message.error(err.message);
        })
        .then(() => {
          this.switchBusy = null;
          if (options?.fromOpDialog) this.opDialog.confirming = false;
        });
    },
  },
});
</script>

<style lang="scss" scoped>
.complaint-drawer__title {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 24px;
}

.complaint-drawer__title-text {
  font-size: 16px;
  font-weight: 600;
  color: $color-text;
  line-height: 1.35;
}

.complaint-drawer__subtitle {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: $color-text-secondary;
}

.complaint-drawer__title-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.live-drawer__shell {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  width: 100%;
}

.live-drawer__body {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 4px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.live-drawer__section {
  flex-shrink: 0;
  border: 1px solid $color-border;
  border-radius: $border-radius-base;
  background: $color-surface;
  overflow: visible;

  &--muted {
    background: #fafbfc;
  }
}

.live-drawer__section-head {
  padding: 12px 14px 10px;
  border-bottom: 1px solid $color-hairline;
  background: #f8fafc;
}

.live-drawer__section-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: $color-text;
  line-height: 1.35;
}

.live-drawer__form {
  display: grid;
  grid-template-columns: var(--live-drawer-label-width) minmax(0, 1fr) var(--live-drawer-label-width) minmax(0, 1fr);
  row-gap: 14px;
  padding: 12px 14px 16px;
}

.live-drawer__row {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  column-gap: 8px;
  align-items: start;
}

@supports not (grid-template-columns: subgrid) {
  .live-drawer__row {
    grid-template-columns: var(--live-drawer-label-width) minmax(0, 1fr) var(--live-drawer-label-width) minmax(0, 1fr);
  }
}

.live-drawer__label {
  justify-self: stretch;
  box-sizing: border-box;
  padding: 8px 8px 0 0;
  font-size: 12px;
  line-height: 1.35;
  color: $color-text-secondary;
  text-align: right;
  white-space: nowrap;
}

.live-drawer__control {
  min-width: 0;
}

.live-drawer__row ::v-deep .el-form-item {
  display: contents;
  margin-bottom: 0;
}

.live-drawer__row ::v-deep .el-form-item__label {
  grid-column: 1;
  justify-self: stretch;
  float: none;
  width: auto !important;
  padding: 8px 8px 0 0;
  font-size: 12px;
  line-height: 1.35;
  color: $color-text-secondary;
  text-align: right;
}

.live-drawer__row ::v-deep .el-form-item__content {
  grid-column: 2 / -1;
  margin-left: 0 !important;
  min-width: 0;
  line-height: normal;
}

.live-drawer__row ::v-deep .el-form-item__error {
  grid-column: 2 / -1;
  padding-top: 2px;
}

.live-drawer__full {
  width: 100%;
}

.live-drawer__date {
  width: 118px;
  max-width: 100%;
}

.live-drawer__fzr-select ::v-deep .el-select__tags {
  max-width: 100%;
}

.live-drawer__section-desc {
  margin: 4px 0 0;
  font-size: 11px;
  line-height: 1.4;
  color: $color-text-secondary;
}

.live-drawer__op-stages {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  padding: 12px 14px 14px;
}

.live-drawer__footer {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid $color-border;
  background: $color-surface;
  box-shadow: 0 -4px 12px rgba(31, 39, 51, 0.04);
}

.live-drawer__footer-side {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  &--end {
    margin-left: auto;
  }
}

.conflict-pick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  font-size: 12px;
  color: $color-text-secondary;
}
</style>

<style lang="scss">
/* 外壳 flex 见 complaint-detail-drawer 中 .el-drawer.complaint-drawer */
.el-drawer.live-case-drawer .live-drawer__shell {
  flex: 1;
  min-height: 0;
}
</style>
